import type { Messages } from './en';

/**
 * Món de les fases B/C: cases que es poden visitar, el mercat nocturn, festes i excursions.
 * Les frases marcades com a pràctica d'anglès es queden en anglès a propòsit.
 */
export const caWorld3: Pick<Messages, 'world3'> = {
  world3: {
    mapNames: { grandma: 'Casa de l’àvia Lee', bo: 'Casa d’en Bo', market: 'Mercat nocturn dels Fanalets' },
    zones: { grandma: 'Casa de l’àvia Lee', bo: 'Casa d’en Bo', market: 'Mercat nocturn', marketGames: 'Parades de jocs' },
    objects: {
      grTea: 'Taula del te',
      grCooker: 'Olla d’arròs',
      grPhotos: 'Fotos de família',
      grSewing: 'Màquina de cosir',
      grCalendar: 'Calendari de fulls',
      boConsole: 'Videoconsola',
      boAquarium: 'Aquari',
      boComics: 'Prestatge de còmics',
      boLego: 'Blocs de construcció',
      mkTea: 'Parada de te de bombolles',
      mkChicken: 'Parada de pollastre fregit',
      mkBalls: 'Boletes de moniato',
      mkTofu: 'Parada de tofu pudent',
      mkOyster: 'Parada de truita d’ostres',
      mkIce: 'Parada de gel picat',
      mkFish: 'Pesca de peixets',
      mkRings: 'Llançament d’anelles',
      mkClaw: 'Màquines de ganxo',
      marketGate: 'Entrada del mercat nocturn',
      fsRiddles: 'Endevinalles dels fanalets',
      fsTangyuan: 'Carretó de tangyuan',
      fsBoat: 'Barques de drac',
      fsZongzi: 'Taula de zongzi',
      fsEgg: 'Taula de l’ou dret',
      fsBBQ: 'Barbacoa de la Festa de la Lluna',
      fsPomelo: 'Cistella de pomelos',
      fsLion: 'Dansa del lleó',
      fsReunion: 'Sopar de Cap d’Any',
    },
    npc: { boMom: 'Mare d’en Bo', teaVendor: 'Tieta Hua', chickenVendor: 'Tiet Kang', gameVendor: 'Tiet Lou', shopper1: 'Client', shopper2: 'Client', shopper3: 'Client' },

    closed: {
      grandma: 'No respon ningú. L’àvia Lee sol ser a casa al vespre, a partir de les 6.',
      bo: 'No hi ha ningú a casa. La família d’en Bo torna a última hora de la tarda.',
      market: 'El mercat nocturn obre a les 5 de la tarda. Torna aquest vespre!',
    },

    grandma: {
      hello: 'Passa, passa! Treu-te les sabates i seu. Vols una mica de te?',
      teaText: 'L’àvia serveix te oolong en tasses petitones. «Primer ensuma’l i després beu a glopets.»',
      drink: '🍵 Prendre te amb l’àvia',
      teaDone: 'Calentó i aromàtic. L’àvia t’explica com eren els turons de te on va créixer.',
      teaWords: 'Pràctica d’anglès: “This tea smells lovely. Thank you, Grandma!”',
      cooker: 'La famosa olla d’arròs vermella. L’àvia diu que és més vella que la teva mare… i que encara fa els millors panets al vapor!',
      steam: '🥟 Fer panets al vapor junts',
      steamDone: 'Panets de porc ben esponjosos! L’àvia n’embolica dos per a la teva família.',
      photos: 'Fotos antigues en blanc i negre: l’àvia de petita en una granja, un casament, un nadó amb una manteta vermella…',
      readStory: '📖 Escoltar el conte de l’àvia',
      sewing: 'Una màquina de cosir antiga amb pedal. Clac, clac, clac!',
      sewLearn: '🧵 Aprendre a cosir un botó',
      sewDone: 'Has cosit el teu primer botó! L’àvia et regala una bufandeta feta a mà.',
      calendar: (date: string) => `Un calendari gruixut de fulls per arrencar. La pàgina d’avui: ${date}. També diu quins dies porten sort!`,
      tearPage: '📅 Arrencar la pàgina d’avui',
      tearDone: 'Rrrec! L’àvia riu: «És el millor moment del dia.»',
      noGrandma: 'L’àvia Lee no hi és ara mateix.',
      mochi: 'La Mochi, la gateta, està arraulida dins una cistella, fent ronc-ronc.',
      byeGift: 'Abans que marxis, l’àvia et dona una bossa de taronges. «Per a la teva família!»',
    },
    bo: {
      momHello: 'Oh, ets l’amic d’en Bo! Gràcies per ajudar-lo amb l’estel. Vols uns dumplings?',
      momDumplings: '🥟 Sí, si us plau! (en anglès: “Yes, please. They smell great!”)',
      momDone: 'Dumplings sucosos amb una mica de vinagre. La mare d’en Bo somriu: «Torna aviat!»',
      console: 'La videoconsola d’en Bo. «Juguem junts!»',
      playConsole: '🎮 Jugar una partida',
      aquarium: 'Tetres neó i una tortugueta mandrosa. En Bo li ha posat de nom «Coet».',
      feedFish: '🐟 Donar menjar als peixos',
      fed: 'Els peixos pugen com fletxes a buscar el menjar. La tortuga Coet no té gens de pressa.',
      comics: 'Els còmics d’en Bo, en xinès i en anglès. Algunes pàgines tenen dibuixets als marges.',
      readComic: '📖 Llegir un còmic',
      lego: 'Una capsa de blocs de construcció i una estació espacial a mig muntar.',
      build: '🧱 Construir amb en Bo',
      built: 'En Bo i tu acabeu l’estació espacial. «Tres, dos, u… enlairament!»',
      boAway: 'En Bo no és a casa ara mateix.',
    },

    fest: {
      names: { lantern: 'Festa dels Fanalets', dragonBoat: 'Festa de les Barques de Drac', moon: 'Festa de la Lluna (Mig Tardor)', newYear: 'Any Nou Lunar' },
      announce: (name: string) => `🎉 Setmana de festa: ${name}! Ves al parc a celebrar-ho.`,
      riddles: {
        intro: 'Pertot arreu pengen fanalets vermells amb endevinalles. Endevina-les i guanyaràs un premi! (Les endevinalles són en anglès.)',
        guess: '🏮 Endevinar una endevinalla',
        right: '🎉 Correcte! El guardià dels fanalets et dona un adhesiu.',
        wrong: 'Aquesta vegada no… torna-la a llegir!',
        allDone: 'Avui has resolt totes les endevinalles. Torna demà!',
        list: [
          { q: 'I have hands but I can’t clap. What am I?', a: ['A clock', 'A tree', 'A shoe'] },
          { q: 'The more you take, the more you leave behind. What am I?', a: ['Footsteps', 'Cookies', 'Money'] },
          { q: 'I’m tall when I’m young and short when I’m old. What am I?', a: ['A candle', 'A dog', 'A book'] },
          { q: 'What has keys but can’t open any door?', a: ['A piano', 'A car', 'A teacher'] },
          { q: 'What goes up but never comes down?', a: ['Your age', 'A ball', 'Rain'] },
          { q: 'What has a neck but no head?', a: ['A bottle', 'A snake', 'A chair'] },
          { q: 'I’m full of holes, but I can still hold water. What am I?', a: ['A sponge', 'A cup', 'A net'] },
          { q: 'What gets wetter the more it dries?', a: ['A towel', 'The sun', 'A fish'] },
          { q: 'What can you catch but not throw?', a: ['A cold', 'A ball', 'A bus'] },
        ],
      },
      tangyuan: 'Tangyuan calentons: boletes dolces d’arròs en sopa de gingebre. Són rodones com la lluna plena i volen dir que la família està unida.',
      eatTangyuan: '🥣 Menjar-se’n un bol',
      tangyuanDone: 'Toves i dolces, farcides de sèsam negre. Se t’escalfa la panxa!',
      boat: 'Dues barques de drac llargues es gronxen a l’estany. El del tambor et saluda: «Vols participar a la cursa?»',
      race: '🐉 Participar a la cursa',
      zongzi: 'Fulles de bambú, arròs enganxós, bolets i cacauets. Embolquem zongzi! Què va primer?',
      zongziSteps: [
        { q: 'Pas 1?', a: ['Doblegar dues fulles de bambú en forma de cucurutxo', 'Lligar el cordill', 'Coure’l al vapor'] },
        { q: 'Pas 2?', a: ['Omplir-lo d’arròs i farciment', 'Menjar-se’l', 'Llençar les fulles'] },
        { q: 'Pas 3?', a: ['Tancar les fulles i lligar-les amb cordill', 'Posar-lo a la nevera', 'Pintar-lo'] },
      ],
      zongziWrong: 'Mmm, això encara no. Pensa-hi una altra vegada!',
      zongziDone: 'Els teus zongzi van a la vaporera gran. Una hora després: boníssims!',
      egg: 'Una tradició antiga: diuen que just a migdia de la Festa de les Barques de Drac pots posar un ou dret!',
      tryEgg: '🥚 Intentar posar un ou dret',
      eggNoon: 'Encara no és migdia. Torna entre les 11:00 i les 13:00!',
      eggWin: 'S’aguanta! Tothom aplaudeix. Bona sort per a tot l’any!',
      eggFail: 'Trontolla… trontolla… i rodola. Torna-hi!',
      bbq: 'Els veïns couen panotxes, salsitxes i calamars en barbacoes petitones mentre surt la lluna.',
      joinBBQ: '🌽 Anar a la barbacoa',
      bbqDone: 'Compartiu pastissos de lluna i blat de moro a la brasa sota la lluna plena. «Bona Festa de la Lluna!»',
      pomelo: 'Una cistella de pomelos enormes. La mainada es posa la pell com un barret divertit!',
      pomeloHat: '🍈 Fer un barret de pomelo',
      pomeloDone: 'Portes un barret de pell de pomelo. Tothom riu… i la fruita és sucosa i dolça!',
      lion: 'Tambors i platerets! Un lleó daurat balla, salta i parpelleja amb els seus ullassos.',
      watchLion: '🦁 Mirar i animar',
      lionDone: 'El lleó et fa una reverència i sacseja la crinera. Això vol dir bona sort!',
      reunion: 'Tota la família al voltant de la taula: peix, dumplings, hot pot i fideus llargs per a una vida llarga.',
      eatReunion: '🍲 Fer el sopar de retrobament',
      reunionDone: 'La mare i el pare et donen un sobre vermell: +88 monedes! Digues-ho en anglès: “Happy New Year! Thank you!”',
      reunionAgain: 'Aquest Any Nou ja has fet el sopar de retrobament.',
    },

    stories: {
      title: '📖 L’hora del conte',
      pick: 'Tria un conte. Toca 🔊 per sentir qualsevol frase, o deixa que te’l llegeixi sencer.',
      readAll: '▶ Llegeix-me’l',
      stop: '⏹ Atura',
      gistBtn: '🌐 De què va?',
      quiz: '❓ Preguntes',
      question: (n: number, total: number) => `Pregunta ${n} de ${total}`,
      right: '✅ Correcte!',
      wrong: '❌ No ben bé… torna a mirar el conte.',
      result: (score: number, total: number) => `N’has encertat ${score} de ${total}!`,
      again: '🔁 Torna a llegir',
      list: '📚 Tots els contes',
      locked: '🔒 Troba aquest conte en algun lloc del món',
      read: '✅ Llegit',
      gists: {
        kite: 'L’estel d’en Bo es queda encallat en un arbre, i un amic l’ajuda a recuperar-lo.',
        mochi: 'La gateta de l’àvia Lee es perd perseguint una papallona.',
        market: 'La Lily és tímida, però demana un te de bombolles en anglès al mercat nocturn.',
        dragon: 'Com són les curses de barques de drac: guanya el treball en equip!',
        grandma: 'L’àvia Lee recorda quan es feia gran en una plantació de te.',
        comic: 'Un còmic divertit: una tortugueta construeix un coet.',
        tower: 'Curiositats del Taipei 101: l’ascensor superràpid i la bola daurada gegant.',
        gorge: 'La gorja de Taroko, a Hualien: penya-segats de marbre i un riu turquesa.',
      },
    },

    market: {
      welcome: '🏮 Benvingut al mercat nocturn! Demana el menjar en anglès i et guanyaràs un somriure (i de vegades alguna coseta de regal).',
      look: 'Només miro',
      orderPrompt: 'Com ho demanes? Tria la millor frase en anglès.',
      wrong: [
        'El venedor riu: «Ha! Prova de dir-ho amb educació!»',
        '«Perdona? Ho pots repetir amb més educació?»',
        'El venedor aixeca una cella. Prova amb una frase més amable!',
      ],
      cost: (n: number) => `${n} monedes`,
      poor: 'No tens prou monedes.',
      extra: '«Que bé que parles anglès! Té, una miqueta més de regal.»',
      playFor: (n: number) => `▶ Jugar (${n} monedes)`,
      gamesText: 'Llums, campanes i premis! Cada partida costa unes quantes monedes.',
      clawText: 'Una filera de màquines de ganxo il·luminades, plenes de peluixos rodons i tous.',
      crowd: [
        'Quantes olors! No sé què menjar primer.',
        'El que més m’agrada són les boletes de moniato. Cruixents per fora i toves per dins!',
        'Has tastat el tofu pudent? Fa una olor forta, però és boníssim.',
        'He guanyat un peluix a la màquina de ganxo! Bé… després de dotze intents.',
        'No t’oblidis de guardar-te les deixalles i llençar-les a les papereres.',
        'El tiet dels peixets diu que el truc és moure la xarxeta a poc a poc.',
      ],
      stalls: {
        mkTea: {
          sign: '珍珠奶茶',
          food: 'bubble tea',
          vendor: 'Hi there! What can I get you?',
          ok: 'Could I have a bubble tea with less sugar, please?',
          bad: ['Give me tea.', 'Bubble! Bubble! Now!'],
          done: 'Dolç, fresquet i ple de perles toves. Xuuup!',
          price: 30,
        },
        mkChicken: {
          sign: '炸雞排',
          food: 'fried chicken cutlet',
          vendor: 'Fresh chicken cutlet, bigger than your face! Spicy or not spicy?',
          ok: 'Not spicy, please. Can you cut it for me?',
          bad: ['Chicken. Fast.', 'I want the biggest one for free.'],
          done: 'Cruixent, sucós i ENORME. Necessites les dues mans!',
          price: 40,
        },
        mkBalls: {
          sign: '地瓜球',
          food: 'sweet potato balls',
          vendor: 'Sweet potato balls, hot and crunchy! Small bag or big bag?',
          ok: 'A small bag, please. Thank you!',
          bad: ['Balls! Give!', 'I don’t like potatoes, give me a bag.'],
          done: 'S’inflen com globets daurats. Cruixents i toves!',
          price: 20,
        },
        mkTofu: {
          sign: '臭豆腐',
          food: 'stinky tofu',
          vendor: 'Stinky tofu! It smells strong, but it’s delicious. Want to try?',
          ok: 'Sure, I’d like to try it, please!',
          bad: ['Ew, it stinks!', 'Make it not stink.'],
          done: 'Cruixent per fora, tou per dins, amb col confitada. Sorprenentment bo!',
          price: 35,
        },
        mkOyster: {
          sign: '蚵仔煎',
          food: 'oyster omelette',
          vendor: 'Oyster omelette with sweet sauce! For here or to go?',
          ok: 'For here, please. One oyster omelette.',
          bad: ['Omelette now!', 'I want it to go and to stay.'],
          done: 'Amb molt d’ou, melosa i una mica dolça. Un clàssic del mercat nocturn!',
          price: 45,
        },
        mkIce: {
          sign: '芒果冰',
          food: 'mango shaved ice',
          vendor: 'Mango shaved ice — mountains of mango! Would you like condensed milk on top?',
          ok: 'Yes, please! Could I also have an extra spoon for my friend?',
          bad: ['Ice. Mango. Go.', 'No milk, no mango, just give me ice.'],
          done: 'Gel esponjós, mango dolç i un fred al cervell la mar de feliç!',
          price: 50,
        },
      },
    },
  },
};
